import { DateTime } from 'luxon';

import { Alerts, Arrows, Communication, General, Media } from '@poseidon/components/StrokeIcons';

import type { FilterKey } from './types';

export const DEFAULT_LIMIT = 50;

export const DEFAULT_RANGE_START = () => DateTime.now().minus({ months: 3 }).startOf('day');
export const DEFAULT_RANGE_END = () => DateTime.now();

export const parseDateString = (value?: string) => {
  if (!value) return undefined;
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt : undefined;
};

export const parseDateRange = (value?: string) => {
  if (!value) return { from: undefined, to: undefined };
  const [from, to] = value.split('.', 2);
  return { from: parseDateString(from), to: parseDateString(to) };
};

export const formatDate = (date: DateTime) => date.toFormat('yyyy-MM-dd');

export const getDefaultRangeStrings = () => ({
  from: formatDate(DEFAULT_RANGE_START()),
  to: formatDate(DEFAULT_RANGE_END()),
});

/**
 * The range a URL (or saved filter) asks for, made sane: neither end is in the future,
 * and a start after the end is swapped so old or hand-edited links still show results.
 * A missing start defaults to three months before the end, so only explicit ends get swapped.
 */
export const resolveDateRange = (query: { dateRange?: string; from?: string; to?: string }) => {
  const parsedRange = parseDateRange(query.dateRange);
  const now = DateTime.now();
  const toDate = DateTime.min(parseDateString(query.to) ?? parsedRange.to ?? DEFAULT_RANGE_END(), now);
  const fromDate = DateTime.min(
    parseDateString(query.from) ?? parsedRange.from ?? toDate.minus({ months: 3 }).startOf('day'),
    now.startOf('day'),
  );
  if (fromDate > toDate.startOf('day')) return { fromDate: toDate.startOf('day'), toDate: fromDate };
  return { fromDate, toDate };
};

export function filterButtonOptions(valueKey: FilterKey) {
  switch (valueKey) {
    case 'response':
      return [
        { value: 'ever-responded', label: 'Lead responded' },
        { value: 'never-responded', label: 'Lead not responded' },
      ] as const;
    case 'states':
      return [
        { value: '', label: 'All Campaigns' },
        { value: 'active', label: 'Active', icon: <General.Zap className="size-4" /> },
        { value: 'paused', label: 'Paused', icon: <Media.PauseCircle className="size-4" /> },
        { value: 'action-required', label: 'Needs Action', icon: <Alerts.Bell01 className="size-4" /> },
        { value: 'responded', label: 'Replied', icon: <Communication.MessageSquare01 className="size-4" /> },
        { value: 'interrupted', label: 'Interrupted', icon: <Alerts.AlertCircle className="size-4" /> },
        { value: 'finished', label: 'Finished', icon: <General.CheckCircle className="size-4" /> },
        { value: 'pending-review', label: 'Pending Review', icon: <General.Eye className="size-4" /> },
      ] as const;
    case 'order':
      return [
        { label: 'Old ▸ New', value: 'asc', icon: <Arrows.ArrowSquareUp className="size-4" /> },
        { label: 'New ▸ Old', value: 'desc', icon: <Arrows.ArrowSquareDown className="size-4" /> },
      ] as const;
  }
}
